/**
 * Screen 09 — suppliers.
 *
 * The prototype's three tabs, kept: who they are, what they carry, and what
 * quantities each order type proposes. The list on the left is the org's, not
 * the venue's — one rep and one trade price across the group.
 *
 * "On hand" and the proposed quantities are computed with `suggestTopUp` from
 * @baros/core, the same function the API runs behind
 * `GET /orders/suggest`, so the figure a manager reads here and the figure the
 * order screen fills in are the same number by construction rather than by
 * two people writing the same arithmetic twice.
 */

import { hasFeature } from "../features.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ORDER_TYPES,
  heldUnitsFrom,
  suggestTopUp,
  type OrderType,
  type TopUpItem,
} from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { money } from "../format.js";
import type { ScreenProps } from "../screens.js";
import { ordersApi, type Supplier, type SupplierItem } from "./api.js";
import { ItemForm } from "./ItemForm.js";
import { SupplierForm } from "./SupplierForm.js";
import "./suppliers.css";

/** What the manager is editing, if anything. */
type Mode =
  | { readonly kind: "new-supplier" }
  | { readonly kind: "edit-supplier" }
  | { readonly kind: "item"; readonly item: SupplierItem | null };

const TABS = ["Details", "What they supply", "Order quantities"] as const;
type Tab = (typeof TABS)[number];

const dollars = (cents: number) => money(cents / 100);

/** `1 case`, `2 cases` — the prototype's pluralisation, in one place. */
const count = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** What one pack is called: a case when it holds several, the unit otherwise. */
const packWord = (item: Pick<SupplierItem, "packSize" | "unit">) =>
  item.packSize > 1 ? "case" : item.unit;

export function SuppliersScreen(_props: ScreenProps) {
  const app = useApp();
  const { token, venueId, go } = app;
  const canEdit = app.me.can["supplier:edit"] ?? false;

  const [suppliers, setSuppliers] = useState<readonly Supplier[]>([]);
  const [heldMl, setHeldMl] = useState<ReadonlyMap<string, number>>(new Map());
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("Details");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** The suppliers, and the venue's stock levels beside them. */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await ordersApi.suppliers(token);
      setSuppliers(list.suppliers);
      setSelected((current) => current ?? list.suppliers[0]?.supplierId ?? null);

      if (venueId) {
        const levels = await ordersApi.stock(token, venueId);
        setHeldMl(new Map(levels.stock.map((s) => [s.ingredientId, s.heldMl])));
      }
    } catch (err) {
      setError(message(err));
    } finally {
      setLoading(false);
    }
  }, [token, venueId]);

  useEffect(() => {
    void load();
  }, [load]);

  const supplier = suppliers.find((s) => s.supplierId === selected) ?? suppliers[0];

  // ---- editing. Each handler saves, reloads, and leaves a one-line notice.
  async function createSupplier(body: Record<string, unknown>) {
    const { supplier: created } = await ordersApi.createSupplier(token, body);
    setMode(null);
    setSelected(created.supplierId);
    setTab("What they supply");
    setNotice(`${created.name} added. Now add the lines you buy from them.`);
    await load();
  }
  async function updateSupplier(body: Record<string, unknown>) {
    if (!supplier) return;
    await ordersApi.saveSupplier(token, supplier.supplierId, body);
    setMode(null);
    setNotice("Supplier details saved.");
    await load();
  }
  async function saveItem(itemId: string, body: Record<string, unknown>) {
    if (!supplier) return;
    await ordersApi.saveItem(token, supplier.supplierId, itemId, body);
    setMode(null);
    setNotice(`${String(body["name"])} saved.`);
    await load();
  }
  async function deleteSupplier() {
    if (!supplier) return;
    const n = supplier.items.length;
    if (!window.confirm(`Delete ${supplier.name} and the ${n} line${n === 1 ? "" : "s"} it carries? Orders already raised keep their lines.`)) return;
    try {
      await ordersApi.deleteSupplier(token, supplier.supplierId);
      setSelected(null);
      setMode(null);
      setNotice(`${supplier.name} deleted.`);
      await load();
    } catch (err) {
      setError(message(err));
    }
  }

  async function removeItem(item: SupplierItem) {
    if (!supplier) return;
    if (!window.confirm(`Remove ${item.name} from ${supplier.name}? Orders already raised keep their lines.`)) return;
    try {
      await ordersApi.deleteItem(token, supplier.supplierId, item.itemId);
      setNotice(`${item.name} removed.`);
      await load();
    } catch (err) {
      setError(message(err));
    }
  }

  /**
   * The supplier's lines with the venue's holding attached.
   *
   * A line with no `STOCK#` row keeps `heldUnits` undefined rather than zero:
   * never counted is not the same as counted and empty, and the top-up treats
   * them differently on purpose.
   */
  const topUpItems: readonly TopUpItem[] = useMemo(() => {
    if (!supplier) return [];
    return supplier.items.map((item) => {
      const held =
        item.ingredientId && item.unitMl
          ? heldUnitsFrom(heldMl.get(item.ingredientId), item.unitMl)
          : undefined;
      return {
        ingredientId: item.ingredientId,
        name: item.name,
        pack: item.pack,
        unitCostCents: item.unitCostCents,
        par: item.par,
        packSize: item.packSize,
        unit: item.unit,
        ...(held === undefined ? {} : { heldUnits: held }),
      };
    });
  }, [supplier, heldMl]);

  /** One column of the quantities tab per order type. */
  const quantities = useMemo(
    () =>
      ORDER_TYPES.map((type) => ({
        type,
        lines: suggestTopUp(topUpItems, type),
      })),
    [topUpItems],
  );

  if (loading && suppliers.length === 0) {
    return (
      <div className="pad label">Reading the supplier list…</div>
    );
  }

  if (!supplier) {
    return (
      <div className="pad" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="msg">
          No suppliers yet.{canEdit ? " Add the first one below." : " A manager can add them."}
        </div>
        {canEdit && <SupplierForm onCancel={() => undefined} onSave={createSupplier} />}
      </div>
    );
  }

  return (
    <div className="screen">
      {error && (
        <div style={{ padding: "12px 20px 0" }}>
          <div className="msg err">{error}</div>
        </div>
      )}
      {notice && (
        <div style={{ padding: "12px 20px 0" }}>
          <div className="msg ok row">
            <span style={{ flex: 1 }}>{notice}</span>
            <button className="btn sm" onClick={() => setNotice(null)}>Dismiss</button>
          </div>
        </div>
      )}

      <div className="sup-split">
        <section className="sup-list">
          <div className="head row">
            <span className="spacer" />
            {canEdit && (
              <button className="btn sm dashed" onClick={() => { setMode({ kind: "new-supplier" }); setTab("Details"); }}>
                + New supplier
              </button>
            )}
          </div>
          <div className="scroll">
            {suppliers.map((s) => (
              <button
                key={s.supplierId}
                className="sup-item"
                aria-pressed={s.supplierId === supplier.supplierId}
                onClick={() => { setSelected(s.supplierId); setMode(null); }}
              >
                <span className="n">{s.name}</span>
                <span className="c">{s.category ?? "Uncategorised"}</span>
                <span className="l">
                  {count(s.items.length, "line")} · {count(s.leadDays ?? 1, "day")} lead
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="pane">
          <div className="sup-head">
            <div>
              <div className="label">{supplier.category ?? "Supplier"}</div>
              <div className="h">{supplier.name}</div>
            </div>
            <span className="spacer" />
            {TABS.map((t) => (
              <button
                key={t}
                className="btn"
                aria-pressed={tab === t}
                onClick={() => setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          <div className="scroll" style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
            {mode?.kind === "new-supplier" && (
              <SupplierForm onCancel={() => setMode(null)} onSave={createSupplier} />
            )}
            {mode?.kind === "edit-supplier" && (
              <SupplierForm initial={supplier} onCancel={() => setMode(null)} onSave={updateSupplier} />
            )}
            {mode?.kind === "item" && (
              <ItemForm
                initial={mode.item ?? undefined}
                ingredients={app.library.ingredients}
                onCancel={() => setMode(null)}
                onSave={saveItem}
              />
            )}

            {tab === "Details" && mode?.kind !== "new-supplier" && mode?.kind !== "edit-supplier" && (
              <>
                <Details supplier={supplier} />
                {canEdit && (
                  <div className="row">
                    <button className="btn" onClick={() => setMode({ kind: "edit-supplier" })}>Edit details</button>
                    <span className="spacer" />
                    <button className="btn danger" onClick={() => void deleteSupplier()}>Delete supplier</button>
                  </div>
                )}
              </>
            )}

            {tab === "What they supply" && canEdit && mode?.kind !== "item" && (
              <div>
                <button className="btn dashed" onClick={() => setMode({ kind: "item", item: null })}>+ Add a line</button>
              </div>
            )}
            {tab === "What they supply" && (
              <div className="table sup-table">
                <div className="thead">
                  <span className="nm">Product</span>
                  <span className="pk">Pack</span>
                  <span className="ct">
                    Pack ex GST
                  </span>
                  <span className="un">
                    Per unit
                  </span>
                  <span className="pr">Par</span>
                  <span className="hd">
                    On hand
                  </span>
                  {canEdit && <span className="act" />}
                </div>
                {supplier.items.map((item, i) => (
                  <ItemRow
                    key={item.itemId}
                    item={item}
                    held={topUpItems[i]?.heldUnits}
                    onEdit={canEdit ? () => setMode({ kind: "item", item }) : undefined}
                    onRemove={canEdit ? () => void removeItem(item) : undefined}
                  />
                ))}
                {supplier.items.length === 0 && (
                  <div className="pad label">Nothing listed yet{canEdit ? " — add the first line above." : "."}</div>
                )}
              </div>
            )}

            {tab === "Order quantities" && (
              <div className="table sup-table">
                <div className="thead">
                  <span className="nm">Product</span>
                  {ORDER_TYPES.map((t) => (
                    <span key={t} className="q">
                      {t}
                    </span>
                  ))}
                </div>
                {supplier.items.map((item, i) => (
                  <div className="sup-row" key={item.itemId}>
                    <span className="nm">{item.name}</span>
                    {quantities.map(({ type, lines }) => {
                      const qty = lines[i]?.qty ?? 0;
                      return (
                        <span key={type} className="q" data-type={type}>
                          {qty === 0 ? "At par" : count(qty, packWord(item))}
                        </span>
                      );
                    })}
                  </div>
                ))}
                <div className="pad label" style={{ lineHeight: 1.7 }}>
                  Against tonight&rsquo;s levels. A line the bar has never counted is
                  proposed a full par; one at or above par is proposed nothing.
                </div>
              </div>
            )}
          </div>

          <div className="sup-foot">
            <div className="label" style={{ fontSize: 11, letterSpacing: "0.08em" }}>
              Orders go out by {channelLabel(supplier)}
            </div>
            <span className="spacer" />
            {hasFeature(app.me, "orders") && (
            <button
              className="btn primary"
              disabled={!venueId}
              onClick={() =>
                go({
                  kind: "order",
                  params: { supplierId: supplier.supplierId, type: ORDER_TYPES[0] },
                })
              }
            >
              Start an order
            </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

/** The prototype's ten-row detail sheet, in two columns. */
function Details({ supplier }: { readonly supplier: Supplier }) {
  const rows: readonly (readonly [string, string])[] = [
    ["Account manager", supplier.rep ?? "—"],
    ["Direct line", supplier.phone ?? "—"],
    ["Orders inbox", supplier.email ?? "—"],
    ["Order method", channelLabel(supplier)],
    ["Payment terms", supplier.terms ?? "—"],
    ["Delivery days", supplier.cutoff ?? (supplier.deliveryDays ?? []).join(", ") ?? "—"],
    [
      "Minimum order",
      supplier.minimumExGstCents ? `${dollars(supplier.minimumExGstCents)} ex GST` : "None",
    ],
    ["Lead time", count(supplier.leadDays ?? 1, "day")],
    ["ABN", supplier.abn ?? "—"],
    ["Our account", supplier.account ?? "—"],
  ];

  return (
    <div className="sup-details">
      {rows.map(([k, v]) => (
        <div className="kv" key={k}>
          <span className="k">{k}</span>
          <span className="v">{v}</span>
        </div>
      ))}
    </div>
  );
}

interface ItemRowProps {
  readonly item: SupplierItem;
  /** Units on hand, or null where the bar has no `STOCK#` row for the line. */
  readonly held: number | null | undefined;
  readonly onEdit?: (() => void) | undefined;
  readonly onRemove?: (() => void) | undefined;
}

function ItemRow({ item, held, onEdit, onRemove }: ItemRowProps) {
  const perUnit = item.packSize > 0 ? item.unitCostCents / item.packSize : item.unitCostCents;

  // The prototype warned when a bottle line dropped below one bottle and when
  // anything else dropped below par. Both are the same question — is there
  // less here than the bar needs — so it is asked once, against par.
  const short = held !== null && held !== undefined && held < item.par;

  return (
    <div className="sup-row">
      <span className="nm">{item.name}</span>
      <span className="pk">{item.pack}</span>
      <span className="ct">{dollars(item.unitCostCents)}</span>
      <span className="un">
        {dollars(perUnit)} / {item.unit}
      </span>
      <span className="pr">{count(item.par, item.unit)}</span>
      <span className={`hd ${short ? "warn" : ""}`}>
        {held === null || held === undefined
          ? "Not counted"
          : `${held.toFixed(1)} ${item.unit} on hand`}
      </span>
      {(onEdit || onRemove) && (
        <span className="act">
          {onEdit && <button className="btn sm" onClick={onEdit}>Edit</button>}
          {onRemove && <button className="btn sm danger" onClick={onRemove}>Remove</button>}
        </span>
      )}
    </div>
  );
}

/** `API · Ordermentum`, `Email · orders@…` — the prototype's `send` string. */
export function channelLabel(supplier: Supplier): string {
  switch (supplier.channel) {
    case "ordermentum":
      return "API · Ordermentum";
    case "phone":
      return `Phone · ${supplier.phone ?? "the rep"}`;
    default:
      return `Email · ${supplier.email ?? "their orders inbox"}`;
  }
}

function message(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : "The supplier list could not be loaded";
}

export function orderTypeOr(value: string | undefined): OrderType {
  return (ORDER_TYPES as readonly string[]).includes(value ?? "")
    ? (value as OrderType)
    : ORDER_TYPES[0];
}
